import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocations } from "@/context/LocationContext";
import { useTheme } from "@/context/ThemeContext";
import { useKiosk } from "@/context/KioskContext";
import useIdleLogout from "@/hooks/useIdleLogout";
import PrinterStatusChip from "@/components/PrinterStatusChip";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  PackagePlus,
  Wheat,
  Truck,
  Boxes,
  ChefHat,
  Factory,
  Box,
  Trash2,
  Search,
  FileBarChart,
  Users,
  Building2,
  Store,
  LogOut,
  SprayCan,
  MapPin,
  Moon,
  Sun,
  Menu,
  X,
  Lock,
  ClipboardCheck,
  Utensils,
} from "lucide-react";

const navGroups = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard" }],
  },
  {
    label: "Inbound",
    items: [
      { to: "/intake", label: "Goods Intake", icon: PackagePlus, perm: "intake" },
      { to: "/materials", label: "Raw Materials", icon: Wheat, perm: "materials" },
      { to: "/suppliers", label: "Suppliers", icon: Building2, perm: "suppliers" },
    ],
  },
  {
    label: "Production",
    items: [
      { to: "/recipes", label: "Recipes", icon: ChefHat, perm: "recipes" },
      { to: "/production", label: "Production Batches", icon: Factory, perm: "production" },
      { to: "/packing", label: "Packing & Labels", icon: Box, perm: "packing" },
      { to: "/waste", label: "Waste", icon: Trash2, perm: "waste" },
      { to: "/cleaning", label: "Cleaning Log", icon: SprayCan, perm: "cleaning" },
      { to: "/knives", label: "Knife Count", icon: Utensils, perm: "knives" },
    ],
  },
  {
    label: "Outbound",
    items: [
      { to: "/inventory", label: "Finished Inventory", icon: Boxes, perm: "inventory" },
      { to: "/dispatch", label: "Dispatch", icon: Truck, perm: "dispatch" },
      { to: "/customers", label: "Customers", icon: Store, perm: "customers" },
    ],
  },
  {
    label: "Quality",
    items: [
      { to: "/trace", label: "Trace & Recall", icon: Search, perm: "trace" },
      { to: "/reports", label: "Reports", icon: FileBarChart, perm: "reports" },
    ],
  },
];

const adminItem = { to: "/admin", label: "Admin", icon: Users, perm: "admin" };

function BrandMark() {
  return (
    <div className="flex items-center gap-3">
      <img
        src="/oz-bakery-logo.png"
        alt="Oz Bakery"
        className="h-12 w-12 rounded-lg object-contain bg-white shadow-sm border border-border"
      />
      <div className="leading-tight">
        <div className="font-display font-bold text-lg">Oz Bakery</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
          Bakery Traceability
        </div>
      </div>
    </div>
  );
}

function SidebarNav({ user, onLinkClick, onLogout }) {
  const { hasPermission } = useAuth();
  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-[15px] transition-colors ${
      isActive
        ? "bg-primary text-primary-foreground font-semibold shadow-sm"
        : "text-stone-700 hover:bg-stone-100"
    }`;
  // Hide tabs the user doesn't have a permission for. Whole groups disappear
  // when every item inside is hidden.
  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.perm || hasPermission(it.perm)),
    }))
    .filter((g) => g.items.length > 0);
  // The Admin panel is reserved for `role === "admin"` only — even if some
  // future migration accidentally seeds the 'admin' key on a staff account,
  // this hard role gate keeps them out.
  const adminVisible = user?.role === "admin" && hasPermission(adminItem.perm);
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-border">
        <BrandMark />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {visibleGroups.map((g) => (
          <div key={g.label}>
            <div className="px-3 mb-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500">
              {g.label}
            </div>
            <div className="space-y-1">
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={onLinkClick}
                  className={linkClass}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <it.icon className="h-5 w-5" />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
        {adminVisible && (
          <div>
            <div className="px-3 mb-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500">
              System
            </div>
            <NavLink to={adminItem.to} onClick={onLinkClick} className={linkClass} data-testid="nav-admin">
              <adminItem.icon className="h-5 w-5" />
              <span>{adminItem.label}</span>
            </NavLink>
            <NavLink to="/stock-take" onClick={onLinkClick} className={linkClass} data-testid="nav-stock-take">
              <ClipboardCheck className="h-5 w-5" />
              <span>Stock Take</span>
            </NavLink>
          </div>
        )}
      </nav>
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm" data-testid="current-user-name">
              {user?.name}
            </div>
            <div className="text-xs uppercase tracking-wider text-stone-500">{user?.role}</div>
          </div>
          <div className="flex items-center gap-1">
            {user?.role === "admin" && <KioskButton />}
            <button
              data-testid="logout-button"
              onClick={onLogout}
              className="h-10 w-10 rounded-lg hover:bg-stone-100 flex items-center justify-center"
              title="Sign out"
            >
              <LogOut className="h-5 w-5 text-stone-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KioskButton() {
  const kiosk = useKiosk();
  if (!kiosk || kiosk.enabled) return null;
  return (
    <button
      type="button"
      onClick={() => {
        if (confirm("Turn on Kiosk Mode? The browser will go full-screen and the admin PIN will be required to exit. Use this for shop-floor tablets.")) {
          kiosk.enter();
        }
      }}
      data-testid="enter-kiosk-button"
      className="h-10 w-10 rounded-lg hover:bg-amber-50 flex items-center justify-center"
      title="Enter Kiosk Mode (admin PIN required to exit)"
    >
      <Lock className="h-5 w-5 text-amber-700" />
    </button>
  );
}


function ThemeToggle() {
  const t = useTheme();
  if (!t) return null;
  const dark = t.theme === "dark";
  return (
    <button
      type="button"
      onClick={t.toggle}
      data-testid="theme-toggle"
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="h-10 w-10 rounded-lg border border-border bg-white dark:bg-stone-800 dark:border-stone-700 flex items-center justify-center hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
    >
      {dark ? <Sun className="h-4 w-4 text-amber-300" /> : <Moon className="h-4 w-4 text-stone-700" />}
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { locations, selected, setLocation } = useLocations();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close drawer on navigation
  useEffect(() => {
    if (drawerOpen) {
      const onResize = () => {
        if (window.innerWidth >= 1024) setDrawerOpen(false);
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
  }, [drawerOpen]);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  // 30-min inactivity logout — heads-up toast at 29 min, hard logout at 30.
  // Disabled on the login page itself (no user → no Layout).
  useIdleLogout({
    enabled: !!user,
    idleMs: 30 * 60 * 1000,
    warnMs: 60 * 1000,
    onLogout: async () => {
      await logout();
      toast.info("Signed out after 30 minutes of inactivity");
      nav("/login");
    },
  });

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-72 shrink-0 bg-white border-r border-border flex-col"
      >
        <SidebarNav user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative w-72 max-w-[85vw] bg-white border-r border-border flex flex-col">
            <button
              type="button"
              className="absolute top-3 right-3 h-9 w-9 rounded-lg hover:bg-stone-100 flex items-center justify-center"
              onClick={() => setDrawerOpen(false)}
              data-testid="drawer-close"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarNav
              user={user}
              onLogout={handleLogout}
              onLinkClick={() => setDrawerOpen(false)}
            />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden h-10 w-10 rounded-lg border border-border bg-white flex items-center justify-center shrink-0"
              onClick={() => setDrawerOpen(true)}
              data-testid="open-drawer"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="lg:hidden">
              <img src="/oz-bakery-logo.png" alt="Oz Bakery" className="h-9 w-9 rounded-md object-contain bg-white border border-border" />
            </div>
            <div className="hidden md:flex items-center gap-2 text-stone-500 text-sm">
              <MapPin className="h-4 w-4" />
              <span className="uppercase tracking-[0.18em] text-xs font-bold">
                Location filter
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <PrinterStatusChip />
            <div className="w-44 sm:w-56 md:w-72 shrink-0">
              <Select
                value={selected || "all"}
                onValueChange={(v) => setLocation(v === "all" ? "" : v)}
              >
                <SelectTrigger className="h-11" data-testid="global-location-select">
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
